import { getApiBaseUrl } from "@/lib/api-base-url";

/**
 * Progsity Learn, the public reads - `LN-HUB01`, docs/35 §4.
 *
 * `GET /v1/learn/tracks` and `GET /v1/learn/tracks/:trackSlug`, both
 * server-side and both cached by the Next data cache for 60 s, the window
 * docs/35 §4 sets for the hub and the track page.
 *
 * **The anonymous payload only.** These helpers send no credentials, so what
 * they return is the half of the route that is the same for everyone, which is
 * what an ISR-cached page is allowed to hold. The viewer half (`viewer` on each
 * card: progress, the Continue row, ownership) is the same route read from the
 * browser with the session, in `LearnViewerOverlay`; a page that read cookies
 * here would opt every locale out of static rendering and cache one learner's
 * progress for the next visitor.
 *
 * Nothing here throws. A hub with no tracks renders an empty state and a track
 * that does not resolve becomes `notFound()`, which is what the caller wants on
 * a 404 and on an API blip alike: a 500 on a marketing page is worse than a
 * page that says there is nothing here yet.
 */
export const LEARN_REVALIDATE_SECONDS = 60;

export type LearnAccessLevel = "free" | "core" | "pro";

export type LearnLesson = {
  id: string;
  title_en: string;
  title_bn: string | null;
  type: string;
  learnAccess: LearnAccessLevel;
  minutes: number | null;
  href: string;
};

export type LearnModule = {
  id: string;
  title_en: string;
  title_bn: string | null;
  lessonCount: number;
  lessons: LearnLesson[];
};

export type LearnPart = {
  key: string;
  title_en: string;
  title_bn: string | null;
  modules: LearnModule[];
};

export type LearnAccessCounts = Record<LearnAccessLevel, number>;

export type LearnTrackCard = {
  courseId: string;
  courseSlug: string;
  trackSlug: string;
  title_en: string;
  title_bn: string | null;
  tagline_en: string | null;
  tagline_bn: string | null;
  thumbnailImageId: string | null;
  difficultyLevel: string | null;
  language: string | null;
  moduleCount: number;
  partCount: number;
  lessonCount: number;
  lessonAccessCounts: LearnAccessCounts;
  estimatedHours: number | null;
  certificateEnabled: boolean;
  planCount: number;
  fromPrice: number | null;
  fromPriceUsd: number | null;
  /** `LN-TST01`: the track's Skill Test has a published version. Absent on an older payload reads as closed. */
  skillTestOpen: boolean;
  updatedAt: string;
};

export type LearnViewer = {
  owned: boolean;
  progress: { completedLessons: number; totalLessons: number; percent: number };
  continueLessonId: string | null;
  /** The viewer's best Skill Test band on this track, or `null` (docs/35 §6, `LN-TST01`). */
  skillBand: string | null;
  /** Track page only. */
  completedLessonIds?: string[];
};

export type LearnHubTrack = LearnTrackCard & { viewer: LearnViewer | null };

export type LearnTrackDetail = LearnTrackCard & { parts: LearnPart[] };

const EMPTY_COUNTS: LearnAccessCounts = { free: 0, core: 0, pro: 0 };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Shape-checked, not trusted: a malformed row would otherwise publish `/learn/undefined`. */
function normalizeCard(raw: unknown): LearnTrackCard | null {
  if (!isRecord(raw)) return null;
  const trackSlug = raw["trackSlug"];
  const courseSlug = raw["courseSlug"];
  const title_en = raw["title_en"];
  if (typeof trackSlug !== "string" || trackSlug.length === 0) return null;
  if (typeof courseSlug !== "string" || typeof title_en !== "string") return null;
  const counts = isRecord(raw["lessonAccessCounts"]) ? raw["lessonAccessCounts"] : {};
  return {
    courseId: String(raw["courseId"] ?? ""),
    courseSlug,
    trackSlug,
    title_en,
    title_bn: typeof raw["title_bn"] === "string" ? raw["title_bn"] : null,
    tagline_en: typeof raw["tagline_en"] === "string" ? raw["tagline_en"] : null,
    tagline_bn: typeof raw["tagline_bn"] === "string" ? raw["tagline_bn"] : null,
    thumbnailImageId:
      typeof raw["thumbnailImageId"] === "string" ? raw["thumbnailImageId"] : null,
    difficultyLevel:
      typeof raw["difficultyLevel"] === "string" ? raw["difficultyLevel"] : null,
    language: typeof raw["language"] === "string" ? raw["language"] : null,
    moduleCount: Number(raw["moduleCount"] ?? 0),
    partCount: Number(raw["partCount"] ?? 0),
    lessonCount: Number(raw["lessonCount"] ?? 0),
    lessonAccessCounts: {
      free: Number(counts["free"] ?? 0),
      core: Number(counts["core"] ?? 0),
      pro: Number(counts["pro"] ?? 0),
    },
    estimatedHours:
      typeof raw["estimatedHours"] === "number" ? raw["estimatedHours"] : null,
    certificateEnabled: raw["certificateEnabled"] === true,
    planCount: Number(raw["planCount"] ?? 0),
    fromPrice: typeof raw["fromPrice"] === "number" ? raw["fromPrice"] : null,
    fromPriceUsd: typeof raw["fromPriceUsd"] === "number" ? raw["fromPriceUsd"] : null,
    skillTestOpen: raw["skillTestOpen"] === true,
    updatedAt: typeof raw["updatedAt"] === "string" ? raw["updatedAt"] : new Date().toISOString(),
  };
}

function normalizeParts(raw: unknown): LearnPart[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((part) => ({
    key: String(part["key"] ?? ""),
    title_en: String(part["title_en"] ?? ""),
    title_bn: typeof part["title_bn"] === "string" ? part["title_bn"] : null,
    modules: (Array.isArray(part["modules"]) ? part["modules"] : [])
      .filter(isRecord)
      .map((module) => ({
        id: String(module["id"] ?? ""),
        title_en: String(module["title_en"] ?? ""),
        title_bn: typeof module["title_bn"] === "string" ? module["title_bn"] : null,
        lessonCount: Number(module["lessonCount"] ?? 0),
        lessons: (Array.isArray(module["lessons"]) ? module["lessons"] : [])
          .filter(isRecord)
          .map((lesson) => ({
            id: String(lesson["id"] ?? ""),
            title_en: String(lesson["title_en"] ?? ""),
            title_bn: typeof lesson["title_bn"] === "string" ? lesson["title_bn"] : null,
            type: String(lesson["type"] ?? "text"),
            learnAccess: (["free", "core", "pro"] as const).includes(
              lesson["learnAccess"] as LearnAccessLevel,
            )
              ? (lesson["learnAccess"] as LearnAccessLevel)
              : "core",
            minutes: typeof lesson["minutes"] === "number" ? lesson["minutes"] : null,
            href: String(lesson["href"] ?? ""),
          })),
      })),
  }));
}

export async function fetchLearnTracks(): Promise<LearnTrackCard[]> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/v1/learn/tracks`, {
      next: { revalidate: LEARN_REVALIDATE_SECONDS },
    });
    if (!res.ok) return [];
    const json = (await res.json()) as { success?: boolean; data?: { tracks?: unknown } };
    if (!json.success || !Array.isArray(json.data?.tracks)) return [];
    return json.data.tracks
      .map(normalizeCard)
      .filter((card): card is LearnTrackCard => card !== null);
  } catch {
    return [];
  }
}

/**
 * One track, or `null` when it does not resolve.
 *
 * Cached by default, for the prebuilt pages (the hub, the track page, the
 * lesson page, the Skill Test intro, the sitemap): a regeneration re-reads a
 * stale entry before it renders, so a withdrawn track's 404 does reach them,
 * and a `no-store` read would make them a 500 at runtime. `live` is for a page
 * that renders per request (the certificate, the readiness check, the Skill
 * Test sitting). There Next's fetch cache, which keeps 200s only, would serve
 * a withdrawn track's last 200 for good, because the refresh's 404 is thrown
 * away (2026-10-20).
 */
export async function fetchLearnTrack(
  trackSlug: string,
  opts: { live?: boolean } = {},
): Promise<LearnTrackDetail | null> {
  try {
    const res = await fetch(
      `${getApiBaseUrl()}/v1/learn/tracks/${encodeURIComponent(trackSlug)}`,
      opts.live ? { cache: "no-store" } : { next: { revalidate: LEARN_REVALIDATE_SECONDS } },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { success?: boolean; data?: { track?: unknown } };
    if (!json.success || !isRecord(json.data?.track)) return null;
    const card = normalizeCard(json.data.track);
    if (!card) return null;
    return { ...card, parts: normalizeParts(json.data.track["parts"]) };
  } catch {
    return null;
  }
}

/** Sum of the three access counts, defensively, for a card rendering a share. */
export function accessShare(counts: LearnAccessCounts = EMPTY_COUNTS): {
  openLessons: number;
  proLessons: number;
} {
  return { openLessons: counts.free + counts.core, proLessons: counts.pro };
}
